'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useBreakpoint } from '@/hooks/use-media-query';
import { Button } from './button';
import { Sheet } from './sheet';

interface ToolbarProps {
  /** Always-visible controls (search, the primary Segmented). */
  children?: ReactNode;
  /**
   * Secondary filters: inline from `md`, behind a "Filters" button and a bottom
   * Sheet below. Only one copy is in the DOM at a time, but which one changes
   * with the viewport, so their state must live in the page (controlled inputs).
   */
  filters?: ReactNode;
  /** Shown on the Filters button — how many filters are off their default. */
  activeFilterCount?: number;
  /** Right-aligned actions. */
  actions?: ReactNode;
  className?: string;
}

/** The list-page toolbar row: wraps at `md`+, folds its filters into a Sheet on phones. */
export function Toolbar({ children, filters, activeFilterCount = 0, actions, className }: ToolbarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Hook-gated rather than CSS-hidden: a hidden inline copy would keep its ids
  // and internal state while the Sheet shows a second copy. SSR renders the
  // inline copy (desktop first); the Sheet can only open after mount.
  const inlineFilters = useBreakpoint('md');
  // Rotating past the switch with the Sheet open would put both copies in the
  // DOM at once — the duplicate ids this gating exists to avoid.
  useEffect(() => {
    if (inlineFilters) setFiltersOpen(false);
  }, [inlineFilters]);
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
      {filters ? (
        <>
          {inlineFilters ? <div className="contents">{filters}</div> : null}
          <Button
            icon={SlidersHorizontal}
            className="md:hidden"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen(true)}
          >
            Filters
            {activeFilterCount > 0 ? (
              <span className="corner-brand-xs bg-accent/15 text-accent-bright px-1.5 text-xs leading-4 tabular-nums">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
          <Sheet
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            title="Filters"
            footer={
              <Button variant="primary" onClick={() => setFiltersOpen(false)}>
                Done
              </Button>
            }
          >
            <div className="grid gap-3 [&>*]:w-full">{filters}</div>
          </Sheet>
        </>
      ) : null}
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
